import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import config from '../../config';

const API_BASE = config.opsApiBase.replace('/api/v1/ops', '');
const CLASS_OPTIONS = Array.from({ length: 18 }, (_, i) => i + 1);
const PAGE_SIZE = 20;

/** Always store/lookup as 91XXXXXXXXXX (12 digits). Accepts 10-digit or +91/91 forms. */
function normalizePhoneForDb(phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  if (digits.startsWith('91') && digits.length === 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return null;
}

const emptyRoute = () => ({
  from: null,
  to: null,
  fromInput: '',
  toInput: '',
  priceMin: '',
  priceMax: '',
});

function formatRoute(route) {
  const fromRaw = (route?.from?.address || '').trim();
  const toRaw = (route?.to?.address || '').trim();
  const from = !fromRaw || /^unknown$/i.test(fromRaw) ? 'others' : fromRaw;
  const to = !toRaw || /^unknown$/i.test(toRaw) ? 'others' : toRaw;
  const min = route?.price_min != null ? route.price_min : route?.priceMin;
  const max = route?.price_max != null ? route.price_max : route?.priceMax;
  const cls = route?.vehicle_class != null ? `Class ${route.vehicle_class} · ` : '';
  const price =
    min == null || min === ''
      ? ''
      : max != null && max !== '' && Number(max) !== Number(min)
        ? ` · ₹${min}–${max}`
        : ` · ₹${min}`;
  return `${cls}${from} → ${to}${price}`;
}

function routeFromApi(route) {
  const from = route?.from || {};
  const to = route?.to || {};
  return {
    from:
      from.lat != null
        ? {
            address: from.address || '',
            lat: from.lat,
            lng: from.lng,
            place_id: from.place_id || null,
          }
        : null,
    to:
      to && to.lat != null
        ? {
            address: to.address || '',
            lat: to.lat,
            lng: to.lng,
            place_id: to.place_id || null,
          }
        : null,
    fromInput: from.address || '',
    toInput: (to && to.address) || '',
    priceMin: route?.price_min != null ? String(route.price_min) : '',
    priceMax:
      route?.price_max != null && Number(route.price_max) !== Number(route.price_min)
        ? String(route.price_max)
        : '',
  };
}

/** Default onboard/edit: shared routes (no vehicle_class). */
function transporterToForm(t) {
  const general = (t.routes || []).filter((r) => r.vehicle_class == null);
  const mapped = general.map(routeFromApi);
  return {
    agencyName: t.agency_name || '',
    contactName: t.contact_name || '',
    phoneNumber: String(t.phone_number || ''),
    vehicleClasses: (t.vehicle_classes || [])
      .map((c) => Number(c))
      .filter((n) => !Number.isNaN(n)),
    routes: mapped.length ? mapped : [emptyRoute()],
    phoneExtensions: (t.phone_extensions || []).map((e) => ({
      phone: String(e.phone || ''),
      label: String(e.label || ''),
    })),
    remarks: t.remarks || '',
  };
}

const blankForm = () => ({
  agencyName: '',
  contactName: '',
  phoneNumber: '',
  vehicleClasses: [],
  routes: [emptyRoute()],
  phoneExtensions: [],
  remarks: '',
});

function buildRoutePayload(r, vehicleClass = null) {
  const hasMin = r.priceMin !== '' && r.priceMin != null && !Number.isNaN(Number(r.priceMin));
  const hasMax = r.priceMax !== '' && r.priceMax != null && !Number.isNaN(Number(r.priceMax));
  const payload = {
    from_place: {
      address: r.from.address,
      latitude: r.from.lat,
      longitude: r.from.lng,
      place_id: r.from.place_id || null,
    },
    to_place:
      r.to?.lat != null
        ? {
            address: r.to.address,
            latitude: r.to.lat,
            longitude: r.to.lng,
            place_id: r.to.place_id || null,
          }
        : null,
    price_min: hasMin ? Number(r.priceMin) : null,
    price_max: hasMax ? Number(r.priceMax) : null,
  };
  if (vehicleClass != null) payload.vehicle_class = vehicleClass;
  return payload;
}

const inputStyle = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  width: '100%',
  fontSize: 14,
};

function RouteEditorRows({ routes, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>Routes &amp; price range *</div>
        <button
          type="button"
          onClick={onAdd}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#2563eb',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          + Add route
        </button>
      </div>
      {routes.map((row, index) => (
        <div
          key={`route-${index}`}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 12,
            marginBottom: 10,
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong style={{ fontSize: 12 }}>Route {index + 1}</strong>
            {routes.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(index)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Remove
              </button>
            )}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>From *</div>
              <PlacesAutocompleteInput
                value={row.fromInput || ''}
                onChange={(text) => onChange(index, { fromInput: text, from: null })}
                onPlaceSelect={(place) =>
                  onChange(index, { from: place, fromInput: place?.address || '' })
                }
                placeholder="Type city / area..."
                inputStyle={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>To (optional)</div>
              <PlacesAutocompleteInput
                value={row.toInput || ''}
                onChange={(text) => onChange(index, { toInput: text, to: null })}
                onPlaceSelect={(place) =>
                  onChange(index, { to: place, toInput: place?.address || '' })
                }
                placeholder="Optional destination..."
                inputStyle={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Price / Min (optional)
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={row.priceMin}
                onChange={(e) => onChange(index, { priceMin: e.target.value })}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Max (optional)
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={row.priceMax}
                onChange={(e) => onChange(index, { priceMax: e.target.value })}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function TransportersTab({ openOnboard = false }) {
  const location = useLocation();
  const [transporters, setTransporters] = useState([]);
  const [search, setSearch] = useState(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('search') || '';
  });
  const [classFilter, setClassFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(!!openOnboard);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(blankForm());

  // Optional class-specific routes modal
  const [showClassRoutesModal, setShowClassRoutesModal] = useState(false);
  const [classRoutesTransporterId, setClassRoutesTransporterId] = useState('');
  const [classRoutesByClass, setClassRoutesByClass] = useState({});
  const [classRoutesClasses, setClassRoutesClasses] = useState([]);
  const [classRoutesError, setClassRoutesError] = useState('');
  const [classRoutesSaving, setClassRoutesSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [vehiclesByTransporter, setVehiclesByTransporter] = useState({});
  const [expandedTransporterIds, setExpandedTransporterIds] = useState(() => new Set());
  const [chatTransporter, setChatTransporter] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatDraft, setChatDraft] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatPaused, setChatPaused] = useState(false);
  const [chatRefreshing, setChatRefreshing] = useState(false);

  const loadTransporterMessages = useCallback(async (transporter) => {
    if (!transporter?.id) return;
    setChatError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/transporters/${transporter.id}/messages`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to load messages');
      }
      const messages = Array.isArray(data.messages) ? data.messages : [];
      messages.sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
      setChatMessages(messages);
      setChatPaused(Boolean(data.bot_paused));
    } catch (e) {
      setChatError(e.message || 'Failed to load messages');
    }
  }, []);

  const openTransporterChat = async (transporter) => {
    setChatTransporter(transporter);
    setChatMessages([]);
    setChatDraft('');
    setChatPaused(false);
    setChatLoading(true);
    await loadTransporterMessages(transporter);
    setChatLoading(false);
  };

  const deleteTransporter = async (transporter) => {
    const name = transporter.agency_name || `T-${transporter.id}`;
    const ok = window.confirm(
      `Delete ${name}? Vehicles and drivers with a different phone are kept and linked to that driver's own user.`
    );
    if (!ok) return;
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/transporters/${transporter.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.detail || 'Failed to delete transporter');
        return;
      }
      setTransporters((prev) => prev.filter((row) => row.id !== transporter.id));
      if (chatTransporter?.id === transporter.id) setChatTransporter(null);
    } catch (e) {
      setError(e.message || 'Failed to delete transporter');
    }
  };

  const refreshTransporterChat = async () => {
    if (!chatTransporter) return;
    setChatRefreshing(true);
    await loadTransporterMessages(chatTransporter);
    setChatRefreshing(false);
  };

  const toggleTransporterManualMode = async () => {
    if (!chatTransporter) return;
    const next = !chatPaused;
    const res = await fetch(`${API_BASE}/api/v1/ops/transporters/${chatTransporter.id}/toggle-bot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        paused: next,
        operator_id: Number(localStorage.getItem('user_id') || 1),
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      setChatError(data.detail || data.message || 'Failed to toggle manual mode');
      return;
    }
    setChatPaused(Boolean(data.bot_paused));
    setTransporters((prev) => prev.map((row) => (
      row.id === chatTransporter.id ? { ...row, bot_paused: Boolean(data.bot_paused) } : row
    )));
  };

  const sendTransporterChat = async () => {
    const text = chatDraft.trim();
    if (!text || !chatTransporter || chatSending) return;
    setChatSending(true);
    setChatError('');
    const optimistic = {
      sender: localStorage.getItem('user_name') || 'Operator',
      role: 'operator',
      message: text,
      content: text,
      timestamp: new Date().toISOString(),
      status: 'pending',
    };
    setChatMessages((prev) => [...prev, optimistic]);
    setChatDraft('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/transporters/${chatTransporter.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          sender: optimistic.sender,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to send message');
      }
      await loadTransporterMessages(chatTransporter);
    } catch (e) {
      setChatError(e.message || 'Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  useEffect(() => {
    if (!chatTransporter) return undefined;
    const timer = setInterval(() => {
      loadTransporterMessages(chatTransporter);
    }, 5000);
    return () => clearInterval(timer);
  }, [chatTransporter, loadTransporterMessages]);

  const loadTransporters = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/transporters`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to load transporters');
      }
      setTransporters(data.transporters || []);
    } catch (e) {
      setError(e.message || 'Failed to load transporters');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const [attentionNow, setAttentionNow] = useState(() => Date.now());

  useEffect(() => {
    loadTransporters();
    const timer = setInterval(() => {
      setAttentionNow(Date.now());
      loadTransporters(true);
    }, 20000);
    return () => clearInterval(timer);
  }, [loadTransporters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/ops/vehicle-drivers`);
        const data = await res.json();
        const rows = Array.isArray(data?.data) ? data.data : [];
        const grouped = {};
        rows.forEach((v) => {
          const tid = v.transporter_id != null ? Number(v.transporter_id) : null;
          if (!tid) return;
          if (!grouped[tid]) grouped[tid] = [];
          grouped[tid].push({
            vehicle_id: v.vehicle_id,
            driver_name: v.driver_name || '',
            registration_no: v.registration_no || '',
          });
        });
        Object.keys(grouped).forEach((tid) => {
          grouped[tid].sort((a, b) => Number(a.vehicle_id) - Number(b.vehicle_id));
        });
        if (!cancelled) setVehiclesByTransporter(grouped);
      } catch {
        if (!cancelled) setVehiclesByTransporter({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleVehicles = (transporterId) => {
    setExpandedTransporterIds((prev) => {
      const next = new Set(prev);
      if (next.has(transporterId)) next.delete(transporterId);
      else next.add(transporterId);
      return next;
    });
  };

  useEffect(() => {
    if (openOnboard) {
      setEditingId(null);
      setForm(blankForm());
      setFormError('');
      setShowModal(true);
    }
  }, [openOnboard]);

  const openCreateModal = () => {
    setEditingId(null);
    setForm(blankForm());
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (t) => {
    setEditingId(t.id);
    setForm(transporterToForm(t));
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setEditingId(null);
    setFormError('');
  };

  const toggleClass = (cls) => {
    setForm((prev) => {
      const has = prev.vehicleClasses.includes(cls);
      return {
        ...prev,
        vehicleClasses: has
          ? prev.vehicleClasses.filter((c) => c !== cls)
          : [...prev.vehicleClasses, cls].sort((a, b) => a - b),
      };
    });
  };

  const updateRoute = (index, patch) => {
    setForm((prev) => {
      const routes = [...prev.routes];
      routes[index] = { ...routes[index], ...patch };
      return { ...prev, routes };
    });
  };

  const validateRoutesList = (list, labelPrefix = 'Route') => {
    if (!list.length) return 'At least one route is required';
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      if (!r.from?.lat) return `${labelPrefix} ${i + 1}: From place is required`;
      if (r.priceMin !== '' && Number(r.priceMin) < 0) {
        return `${labelPrefix} ${i + 1}: price must be >= 0`;
      }
      if (
        r.priceMax !== '' &&
        r.priceMin !== '' &&
        Number(r.priceMax) < Number(r.priceMin)
      ) {
        return `${labelPrefix} ${i + 1}: max must be >= min`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setFormError('');
    const agencyName = form.agencyName.trim();
    const contactName = form.contactName.trim();
    const normalizedPhone = normalizePhoneForDb(form.phoneNumber);
    if (!agencyName) return setFormError('Agency name is required');
    if (!contactName) return setFormError('Contact person name is required');
    if (!normalizedPhone) {
      return setFormError('Enter a valid 10-digit phone (with or without +91)');
    }
    if (!form.vehicleClasses.length) {
      return setFormError('Select at least one vehicle class');
    }
    const routeErr = validateRoutesList(form.routes);
    if (routeErr) return setFormError(routeErr);

    setSaving(true);
    try {
      // Preserve any existing class-specific routes when editing
      let classSpecific = [];
      if (editingId) {
        const existing = transporters.find((t) => t.id === editingId);
        classSpecific = (existing?.routes || []).filter((r) => r.vehicle_class != null);
      }

      const generalPayload = form.routes.map((r) => buildRoutePayload(r, null));
      const classPayload = classSpecific.map((r) => {
        const mapped = routeFromApi(r);
        return buildRoutePayload(mapped, Number(r.vehicle_class));
      });

      const payload = {
        agency_name: agencyName,
        contact_name: contactName,
        phone_number: normalizedPhone,
        vehicle_classes: form.vehicleClasses,
        routes: [...generalPayload, ...classPayload],
        phone_extensions: (form.phoneExtensions || [])
          .map((e) => ({
            phone: String(e.phone || '').trim(),
            label: String(e.label || '').trim(),
          }))
          .filter((e) => e.phone),
        remarks: (form.remarks || '').trim(),
      };
      const url = editingId
        ? `${API_BASE}/api/v1/ops/transporters/${editingId}`
        : `${API_BASE}/api/v1/ops/transporters/onboard`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(
          (typeof data.detail === 'string' && data.detail) ||
            data.message ||
            'Failed to save transporter'
        );
      }
      setShowModal(false);
      setEditingId(null);
      setForm(blankForm());
      await loadTransporters();
    } catch (e) {
      setFormError(e.message || 'Failed to save transporter');
    } finally {
      setSaving(false);
    }
  };

  const openClassRoutesModal = (preselectId = '') => {
    setClassRoutesError('');
    setShowClassRoutesModal(true);
    if (preselectId) {
      loadClassRoutesForTransporter(preselectId);
    } else {
      setClassRoutesTransporterId('');
      setClassRoutesClasses([]);
      setClassRoutesByClass({});
    }
  };

  const loadClassRoutesForTransporter = (id) => {
    const t = transporters.find((x) => String(x.id) === String(id));
    setClassRoutesTransporterId(String(id));
    if (!t) {
      setClassRoutesClasses([]);
      setClassRoutesByClass({});
      return;
    }
    const classes = (t.vehicle_classes || [])
      .map((c) => Number(c))
      .filter((n) => !Number.isNaN(n));
    const byClass = {};
    classes.forEach((c) => {
      byClass[c] = [];
    });
    (t.routes || []).forEach((r) => {
      if (r.vehicle_class == null) return;
      const vc = Number(r.vehicle_class);
      if (!byClass[vc]) byClass[vc] = [];
      byClass[vc].push(routeFromApi(r));
    });
    classes.forEach((c) => {
      if (!byClass[c].length) byClass[c] = [emptyRoute()];
    });
    setClassRoutesClasses(classes);
    setClassRoutesByClass(byClass);
  };

  const updateClassRoute = (cls, index, patch) => {
    setClassRoutesByClass((prev) => {
      const list = [...(prev[cls] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [cls]: list };
    });
  };

  const saveClassRoutes = async () => {
    setClassRoutesError('');
    if (!classRoutesTransporterId) {
      return setClassRoutesError('Select a transporter first');
    }
    const t = transporters.find((x) => String(x.id) === String(classRoutesTransporterId));
    if (!t) return setClassRoutesError('Transporter not found');

    const classPayload = [];
    for (const cls of classRoutesClasses) {
      const list = (classRoutesByClass[cls] || []).filter((r) => r.from?.lat);
      for (let i = 0; i < list.length; i++) {
        const r = list[i];
        if (r.priceMin !== '' && Number(r.priceMin) < 0) {
          return setClassRoutesError(`Class ${cls} route ${i + 1}: price must be >= 0`);
        }
        if (
          r.priceMax !== '' &&
          r.priceMin !== '' &&
          Number(r.priceMax) < Number(r.priceMin)
        ) {
          return setClassRoutesError(`Class ${cls} route ${i + 1}: max must be >= min`);
        }
        classPayload.push(buildRoutePayload(r, cls));
      }
    }

    const general = (t.routes || [])
      .filter((r) => r.vehicle_class == null)
      .map((r) => buildRoutePayload(routeFromApi(r), null));

    setClassRoutesSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/ops/transporters/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agency_name: t.agency_name,
          contact_name: t.contact_name,
          phone_number: String(t.phone_number),
          vehicle_classes: t.vehicle_classes || [],
          routes: [...general, ...classPayload],
          remarks: t.remarks || '',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(
          (typeof data.detail === 'string' && data.detail) ||
            data.message ||
            'Failed to save class routes'
        );
      }
      setShowClassRoutesModal(false);
      await loadTransporters();
    } catch (e) {
      setClassRoutesError(e.message || 'Failed to save class routes');
    } finally {
      setClassRoutesSaving(false);
    }
  };

  const filteredTransporters = useMemo(() => {
    const term = search.trim().toLowerCase();
    const termDigits = term.replace(/\D/g, '');
    const cls = classFilter === '' ? null : Number(classFilter);
    const idMatch = term.match(/^t-?(\d+)$/i);

    return transporters.filter((t) => {
      if (cls != null) {
        const inClasses = (t.vehicle_classes || []).some((c) => Number(c) === cls);
        const inRoutes = (t.routes || []).some((r) => Number(r?.vehicle_class) === cls);
        if (!inClasses && !inRoutes) return false;
      }
      if (!term) return true;

      if (idMatch) {
        return String(t.id) === idMatch[1];
      }

      const haystack = [
        String(t.id),
        `t-${t.id}`,
        t.agency_name,
        t.contact_name,
        t.remarks,
        (t.vehicle_classes || []).map((c) => `class ${c}`).join(' '),
        (t.routes || []).map(formatRoute).join(' '),
        (t.phone_extensions || []).map((e) => `${e.phone} ${e.label || ''}`).join(' '),
      ]
        .join(' ')
        .toLowerCase();
      if (haystack.includes(term)) return true;

      if (termDigits.length >= 4) {
        const phones = [
          String(t.phone_number || ''),
          ...(t.phone_extensions || []).map((e) => String(e.phone || '')),
        ];
        if (phones.some((p) => p.replace(/\D/g, '').includes(termDigits))) return true;
      }
      return false;
    }).sort((a, b) => {
      const at = (row) => {
        const parsed = Date.parse(row.last_inbound_at || '');
        if (!Number.isFinite(parsed)) return null;
        const age = attentionNow - parsed;
        return age >= 0 && age < 10 * 60 * 1000 ? parsed : null;
      };
      const aAt = at(a);
      const bAt = at(b);
      if (aAt && bAt) return bAt - aAt;
      if (aAt) return -1;
      if (bAt) return 1;
      return 0;
    });
  }, [transporters, search, classFilter, attentionNow]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, classFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredTransporters.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedTransporters = filteredTransporters.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0 }}>Transporters</h3>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
            Onboard agency profiles first, then attach vehicles under that transporter.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => openClassRoutesModal()}
            style={{
              padding: '10px 16px',
              background: '#fff',
              color: '#1e40af',
              border: '1px solid #93c5fd',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Optional: set different routes per vehicle class"
          >
            Class routes (optional)
          </button>
          <button
            type="button"
            onClick={openCreateModal}
            style={{
              padding: '10px 16px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Onboard Transporter
          </button>
        </div>
      </div>

      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agency, contact, phone, extension, class, route, remarks"
          style={{ ...inputStyle, width: 360, maxWidth: '100%' }}
        />
        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          style={{ ...inputStyle, width: 150 }}
        >
          <option value="">All classes</option>
          {CLASS_OPTIONS.map((cls) => (
            <option key={cls} value={cls}>
              Class {cls}
            </option>
          ))}
        </select>
        {(search || classFilter) && (
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setClassFilter('');
            }}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid #d1d5db',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
        <span style={{ color: '#6b7280', fontSize: 13 }}>
          {filteredTransporters.length === 0
            ? `0 of ${transporters.length}`
            : `Showing ${Math.min((safePage - 1) * PAGE_SIZE + 1, filteredTransporters.length)}–${Math.min(safePage * PAGE_SIZE, filteredTransporters.length)} of ${filteredTransporters.length}`}
        </span>
      </div>

      {loading ? (
        <div style={{ color: '#6b7280' }}>Loading transporters…</div>
      ) : transporters.length === 0 ? (
        <div
          style={{
            padding: 24,
            border: '1px dashed #d1d5db',
            borderRadius: 8,
            color: '#6b7280',
            textAlign: 'center',
          }}
        >
          No transporters yet. Click <strong>Onboard Transporter</strong> to add one.
        </div>
      ) : filteredTransporters.length === 0 ? (
        <div
          style={{
            padding: 24,
            border: '1px dashed #d1d5db',
            borderRadius: 8,
            color: '#6b7280',
            textAlign: 'center',
          }}
        >
          No transporters match this filter.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="vehicles-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Agency</th>
                <th>Contact</th>
                <th>Phone</th>
                <th>Classes</th>
                <th>Routes</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedTransporters.map((t) => {
                const vehicles = vehiclesByTransporter[t.id] || [];
                const expanded = expandedTransporterIds.has(t.id);
                const inboundAt = Date.parse(t.last_inbound_at || '');
                const incoming = Number.isFinite(inboundAt) && attentionNow - inboundAt >= 0 && attentionNow - inboundAt < 10 * 60 * 1000;
                const paused = Boolean(t.bot_paused);
                return (
                <React.Fragment key={t.id}>
                <tr
                  onClick={() => toggleVehicles(t.id)}
                  style={{
                    cursor: 'pointer',
                    background: incoming ? '#fff7ed' : (paused ? '#fef2f2' : (expanded ? '#f8fafc' : undefined)),
                    boxShadow: incoming ? 'inset 3px 0 0 #f59e0b' : (paused ? 'inset 3px 0 0 #ef4444' : undefined),
                  }}
                  aria-expanded={expanded}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          transform: expanded ? 'rotate(90deg)' : 'none',
                          transition: 'transform 0.15s ease',
                          color: '#6b7280',
                          fontSize: 11,
                        }}
                        aria-hidden="true"
                      >
                        ▸
                      </span>
                      T-{t.id}
                    </div>
                  </td>
                  <td>{t.agency_name}</td>
                  <td>{t.contact_name}</td>
                  <td>
                    <div>{t.phone_number}</div>
                    {(t.phone_extensions || []).map((ext, idx) => (
                      <div key={idx} style={{ fontSize: 11, color: '#6b7280', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span role="img" aria-label="phone">📞</span>
                        <span>{ext.phone}{ext.label ? ` (${ext.label})` : ''}</span>
                      </div>
                    ))}
                  </td>
                  <td>
                    {(t.vehicle_classes || []).map((c) => `Class ${c}`).join(', ') || '—'}
                  </td>
                  <td style={{ maxWidth: 420, fontSize: 12 }}>
                    {(t.routes || []).length
                      ? (t.routes || []).map((r, i) => (
                          <div key={`${t.id}-r-${i}`}>{formatRoute(r)}</div>
                        ))
                      : '—'}
                  </td>
                  <td
                    style={{ maxWidth: 280, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap' }}
                    title={t.remarks || ''}
                  >
                    {t.remarks
                      ? (t.remarks.length > 160 ? `${t.remarks.slice(0, 160)}…` : t.remarks)
                      : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => openTransporterChat(t)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: paused ? '#ef4444' : (incoming ? '#f59e0b' : '#16a34a'),
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        {paused ? 'Paused' : (incoming ? 'Active' : 'Messages')}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEditModal(t)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #d1d5db',
                          background: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => openClassRoutesModal(t.id)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #93c5fd',
                          background: '#eff6ff',
                          color: '#1e40af',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Class routes
                      </button>
                      <Link
                        to={`/app/drivers/new?type=transporter&phone=${encodeURIComponent(
                          String(t.phone_number || '')
                        )}`}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: 'none',
                          background: '#16a34a',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'none',
                          display: 'inline-block',
                        }}
                      >
                        Onboard Vehicle
                      </Link>
                      <button
                        type="button"
                        onClick={() => deleteTransporter(t)}
                        style={{
                          padding: '6px 10px',
                          borderRadius: 6,
                          border: '1px solid #fecaca',
                          background: '#fef2f2',
                          color: '#b91c1c',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={8} style={{ background: '#f8fafc', padding: '12px 16px' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                        Onboarded vehicles ({vehicles.length})
                      </div>
                      {vehicles.length === 0 ? (
                        <span style={{ color: '#6b7280', fontSize: 13 }}>
                          No vehicles onboarded under this transporter yet.
                        </span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {vehicles.map((v) => (
                            <Link
                              key={v.vehicle_id}
                              to={`/app/dispatch/vehicles?tab=vehicles&search=${encodeURIComponent(v.vehicle_id)}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 10px',
                                borderRadius: 8,
                                border: '1px solid #bfdbfe',
                                background: '#fff',
                                color: '#1d4ed8',
                                textDecoration: 'none',
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                              title={`Open vehicle ${v.vehicle_id}`}
                            >
                              V-{v.vehicle_id}
                              {v.driver_name ? ` · ${v.driver_name}` : ''}
                              {v.registration_no ? ` · ${v.registration_no}` : ''}
                              {v.driver_user_id ? ` · user ${v.driver_user_id}` : ''}
                            </Link>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 12,
              padding: '10px 0',
            }}
          >
            <span style={{ color: '#6b7280', fontSize: 13 }}>
              Page {safePage} of {totalPages}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  background: safePage <= 1 ? '#f3f4f6' : '#fff',
                  cursor: safePage <= 1 ? 'not-allowed' : 'pointer',
                  color: '#374151',
                }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid #d1d5db',
                  background: safePage >= totalPages ? '#f3f4f6' : '#fff',
                  cursor: safePage >= totalPages ? 'not-allowed' : 'pointer',
                  color: '#374151',
                }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default onboard / edit modal — shared routes */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '40px 16px',
            overflowY: 'auto',
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              width: '100%',
              maxWidth: 720,
              padding: 24,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                gap: 12,
              }}
            >
              <h3 style={{ margin: 0 }}>
                {editingId ? 'Edit Transporter' : 'Onboard Transporter'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 22,
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
                Agency name *
                <input
                  style={inputStyle}
                  value={form.agencyName}
                  onChange={(e) => setForm({ ...form, agencyName: e.target.value })}
                  placeholder="Agency / company name"
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
                Contact person name *
                <input
                  style={inputStyle}
                  value={form.contactName}
                  onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                  placeholder="Person name"
                />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
                Phone number *
                <input
                  style={inputStyle}
                  value={form.phoneNumber}
                  onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                  placeholder="8861271956 or +918861271956"
                />
              </label>

              {/* Phone Extensions Section */}
              <div style={{ display: 'grid', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Phone extensions (optional)</div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        phoneExtensions: [...(prev.phoneExtensions || []), { phone: '', label: '' }],
                      }))
                    }
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#2563eb',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    + Add extension
                  </button>
                </div>
                {(!form.phoneExtensions || form.phoneExtensions.length === 0) ? (
                  <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', marginBottom: 4 }}>
                    No extensions added. Click "+ Add extension" to add contact numbers.
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: 8, marginBottom: 4 }}>
                    {form.phoneExtensions.map((ext, idx) => (
                      <div key={`ext-${idx}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          placeholder="Phone (10 or 12 digits)"
                          style={{ ...inputStyle, flex: 1 }}
                          value={ext.phone}
                          onChange={(e) => {
                            const updated = [...form.phoneExtensions];
                            updated[idx].phone = e.target.value;
                            setForm({ ...form, phoneExtensions: updated });
                          }}
                        />
                        <input
                          placeholder="Label (e.g. manager)"
                          style={{ ...inputStyle, flex: 1 }}
                          value={ext.label}
                          onChange={(e) => {
                            const updated = [...form.phoneExtensions];
                            updated[idx].label = e.target.value;
                            setForm({ ...form, phoneExtensions: updated });
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = form.phoneExtensions.filter((_, i) => i !== idx);
                            setForm({ ...form, phoneExtensions: updated });
                          }}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#dc2626',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '4px 8px',
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  Vehicle classes *
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {CLASS_OPTIONS.map((cls) => {
                    const selected = form.vehicleClasses.includes(cls);
                    return (
                      <button
                        key={cls}
                        type="button"
                        onClick={() => toggleClass(cls)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: 999,
                          border: selected ? '1px solid #2563eb' : '1px solid #d1d5db',
                          background: selected ? '#eff6ff' : '#fff',
                          color: selected ? '#1d4ed8' : '#374151',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        Class {cls}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600 }}>
                Remarks (optional)
                <textarea
                  style={{ ...inputStyle, minHeight: 88, resize: 'vertical' }}
                  value={form.remarks || ''}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder="Notes, summary, or other details"
                />
              </label>

              <RouteEditorRows
                routes={form.routes}
                onChange={updateRoute}
                onAdd={() =>
                  setForm((prev) => ({ ...prev, routes: [...prev.routes, emptyRoute()] }))
                }
                onRemove={(index) =>
                  setForm((prev) => ({
                    ...prev,
                    routes:
                      prev.routes.length > 1
                        ? prev.routes.filter((_, i) => i !== index)
                        : [emptyRoute()],
                  }))
                }
              />

              {formError && (
                <div style={{ color: '#b91c1c', fontSize: 13 }}>{formError}</div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button
                  type="button"
                  disabled={saving}
                  onClick={closeModal}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleSubmit}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: saving ? 'wait' : 'pointer',
                  }}
                >
                  {saving
                    ? 'Saving…'
                    : editingId
                      ? 'Save changes'
                      : 'Save transporter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Optional class-specific routes modal */}
      {showClassRoutesModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 1210,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '40px 16px',
            overflowY: 'auto',
          }}
          onClick={() => !classRoutesSaving && setShowClassRoutesModal(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              width: '100%',
              maxWidth: 720,
              padding: 24,
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Class routes (optional)</h3>
                <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
                  Set different From / prices per vehicle class. Shared routes from onboard stay
                  unchanged. To is optional; From is required.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowClassRoutesModal(false)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: 22,
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
              Transporter *
              <select
                style={inputStyle}
                value={classRoutesTransporterId}
                onChange={(e) => loadClassRoutesForTransporter(e.target.value)}
              >
                <option value="">Select transporter…</option>
                {transporters.map((t) => (
                  <option key={t.id} value={t.id}>
                    T-{t.id} · {t.agency_name}
                  </option>
                ))}
              </select>
            </label>

            {!classRoutesTransporterId ? (
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                Select a transporter to edit class-specific routes.
              </div>
            ) : !classRoutesClasses.length ? (
              <div style={{ color: '#b45309', fontSize: 13 }}>
                This transporter has no vehicle classes. Edit the transporter and select classes
                first.
              </div>
            ) : (
              classRoutesClasses.map((cls) => (
                <div
                  key={`cls-modal-${cls}`}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 12,
                    background: '#fafafa',
                  }}
                >
                  <strong style={{ fontSize: 13 }}>Class {cls}</strong>
                  <div style={{ marginTop: 8 }}>
                    <RouteEditorRows
                      routes={classRoutesByClass[cls] || [emptyRoute()]}
                      onChange={(index, patch) => updateClassRoute(cls, index, patch)}
                      onAdd={() =>
                        setClassRoutesByClass((prev) => ({
                          ...prev,
                          [cls]: [...(prev[cls] || []), emptyRoute()],
                        }))
                      }
                      onRemove={(index) =>
                        setClassRoutesByClass((prev) => {
                          const list = (prev[cls] || []).filter((_, i) => i !== index);
                          return {
                            ...prev,
                            [cls]: list.length ? list : [emptyRoute()],
                          };
                        })
                      }
                    />
                  </div>
                </div>
              ))
            )}

            {classRoutesError && (
              <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>
                {classRoutesError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                type="button"
                disabled={classRoutesSaving}
                onClick={() => setShowClassRoutesModal(false)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={classRoutesSaving || !classRoutesTransporterId}
                onClick={saveClassRoutes}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: classRoutesSaving ? 'wait' : 'pointer',
                }}
              >
                {classRoutesSaving ? 'Saving…' : 'Save class routes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {chatTransporter && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 80,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setChatTransporter(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(760px, 100%)',
              height: 'min(720px, 92vh)',
              background: '#fff',
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            }}
          >
            <div
              style={{
                padding: '16px 18px',
                background: '#5b21b6',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18, fontWeight: 700 }}>Transporter Conversation</span>
                  <button
                    type="button"
                    onClick={refreshTransporterChat}
                    disabled={chatRefreshing}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 8,
                      border: '1px solid rgba(255,255,255,0.45)',
                      background: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      cursor: chatRefreshing ? 'wait' : 'pointer',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {chatRefreshing ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
                <div style={{ marginTop: 6, fontSize: 13, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: '2px 8px' }}>
                    T-{chatTransporter.id}
                  </span>
                  <span>{chatTransporter.contact_name || chatTransporter.agency_name}</span>
                  <span>{chatTransporter.phone_number}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                  type="button"
                  onClick={toggleTransporterManualMode}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: chatPaused ? '#10b981' : '#ef4444',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {chatPaused ? 'Resume Bot' : 'Manual Mode'}
                </button>
                <button
                  type="button"
                  onClick={() => setChatTransporter(null)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: 'none',
                    background: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontSize: 22,
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            {chatPaused && (
              <div style={{ background: '#fef3c7', color: '#92400e', padding: '10px 16px', fontSize: 13 }}>
                Manual mode is on. The bot will not auto-reply. Messages you send go out on WhatsApp.
              </div>
            )}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: 20,
                backgroundColor: '#f9fafb',
                backgroundImage: 'radial-gradient(circle at 20px 20px, #e5e7eb 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }}
            >
              {chatError && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 8 }}>{chatError}</div>}
              {!chatLoading && chatMessages.length === 0 && (
                <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 80 }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>No messages yet</div>
                  <div style={{ fontSize: 14, marginTop: 8, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto' }}>
                    Messages will be sent via WhatsApp to the transporter&apos;s phone. Start a conversation by typing below.
                  </div>
                </div>
              )}
              {chatMessages.map((msg, idx) => {
                const role = String(msg.role || msg.sender || '').toLowerCase();
                const fromThem = role === 'driver' || role === 'user' || role === 'transporter';
                const text = msg.content || msg.message || '';
                const kind = String(msg.type || '').toLowerCase();
                const isImage = kind === 'image' || /\.(jpg|jpeg|png|webp|gif)(\?|$)/i.test(text);
                const isLocation = kind === 'location' || text.includes('google.com/maps');
                return (
                  <div
                    key={`${msg.timestamp || idx}-${idx}`}
                    style={{ display: 'flex', justifyContent: fromThem ? 'flex-start' : 'flex-end', marginBottom: 10 }}
                  >
                    <div
                      style={{
                        maxWidth: '75%',
                        padding: '10px 12px',
                        borderRadius: 12,
                        background: fromThem ? '#fff' : role === 'bot' ? '#ede9fe' : '#4f46e5',
                        color: fromThem || role === 'bot' ? '#111827' : '#fff',
                        fontSize: 14,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      <div>
                        {isImage && /^https?:\/\//i.test(text) ? (
                          <a href={text} target="_blank" rel="noopener noreferrer">
                            <img src={text} alt="" style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 8, display: 'block' }} />
                          </a>
                        ) : isLocation && /^https?:\/\//i.test(text) ? (
                          <a href={text} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            View location
                          </a>
                        ) : (
                          text || '[empty message]'
                        )}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>
                        {msg.timestamp
                          ? new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                          : ''}
                        {msg.status === 'pending' ? '  ⏳' : ''}
                        {msg.status === 'sent' ? '  ✓' : ''}
                        {msg.status === 'saved' ? '  💾' : ''}
                        {msg.status === 'failed' ? '  ❌' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: 16, borderTop: '1px solid #e5e7eb', background: '#fff' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <textarea
                  value={chatDraft}
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendTransporterChat();
                    }
                  }}
                  placeholder="Type your message to the transporter..."
                  disabled={chatSending}
                  style={{
                    flex: 1,
                    padding: '12px 14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 12,
                    fontSize: 14,
                    resize: 'none',
                    minHeight: 52,
                    fontFamily: 'inherit',
                  }}
                />
                <button
                  type="button"
                  onClick={sendTransporterChat}
                  disabled={!chatDraft.trim() || chatSending}
                  style={{
                    minHeight: 52,
                    padding: '12px 18px',
                    borderRadius: 12,
                    border: 'none',
                    background: chatDraft.trim() && !chatSending ? '#4f46e5' : '#d1d5db',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: chatDraft.trim() && !chatSending ? 'pointer' : 'not-allowed',
                  }}
                >
                  {chatSending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                Press Enter to send, Shift+Enter for a new line
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
